import React from 'react';
import { Button, Switch, Tooltip, Popover, Select, Slider } from '@arco-design/web-react';
import { IconSettings } from '@arco-design/web-react/icon';
import { SettingItem } from '@/lib/hooks/useAppSettings';
import styles from './index.module.less';

interface SettingsPanelProps {
  /** 设置项配置列表 */
  items: SettingItem[];
  /** 额外的样式类名 */
  className?: string;
  /** 设置按钮的样式类名 */
  buttonClassName?: string;
  /** 弹出层位置 */
  position?: 'br' | 'bl' | 'tr' | 'tl' | 'top' | 'bottom' | 'left' | 'right';
  /** 是否将弹出层挂载到body */
  getPopupContainer?: () => HTMLElement;
}

/**
 * 统一的设置面板组件
 * 提供可配置的设置项列表，支持开关和选择器两种类型
 */
function SettingsPanel({
  items,
  className,
  buttonClassName,
  position = 'br',
  getPopupContainer,
}: SettingsPanelProps) {
  // 使用稳定的容器配置，避免ResizeObserver循环
  const stableGetPopupContainer = React.useCallback(() => {
    return getPopupContainer ? getPopupContainer() : document.body;
  }, [getPopupContainer]);

  return (
    <div className={className}>
      <Popover
        trigger="click"
        position={position}
        getPopupContainer={stableGetPopupContainer}
        content={
          <div className={styles.settingsMenu}>
            {items.map((item) => (
              <div key={item.key} className={styles.settingsMenuItem}>
                <Tooltip content={item.tooltip}>
                  <span>{item.label}</span>
                </Tooltip>
                {item.type === 'select' ? (
                  <Select
                    size="small"
                    value={item.value}
                    onChange={(value) => {
                      item.onChange(value);
                    }}
                    className={styles.selectShort}
                    getPopupContainer={stableGetPopupContainer}
                  >
                    {item.options?.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                ) : item.type === 'slider' ? (
                  <div className={styles.sliderContainer}>
                    <Slider
                      value={item.value}
                      min={item.min}
                      max={item.max}
                      step={item.step}
                      onChange={(value) => item.onChange(value)}
                      className={styles.slider}
                    />
                    <span className={styles.sliderValue}>{item.value}px</span>
                  </div>
                ) : (
                  <Switch
                    size="small"
                    checked={item.checked}
                    onChange={(checked) => item.onChange(checked)}
                  />
                )}
              </div>
            ))}
          </div>
        }
      >
        <Button 
          type="text" 
          className={buttonClassName || styles.settingsBtn} 
          icon={<IconSettings />} 
        />
      </Popover>
    </div>
  );
}

export default SettingsPanel;
export type { SettingsPanelProps };